import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SkeletonBlock from "../common/SkeletonBlock";
import EnvironmentalMetricDayChart from "./EnvironmentalMetricDayChart";
import { getEnvironmentalMetricValueClass } from "./environmentalMetricStyles";
import { useUserWebAppPresentationFeatures } from "../../hooks/useUserWebAppPresentationFeatures";
import {
  captureCurrentEnvironmentalContext,
  isEnvironmentalContextEnabled,
  summarizeEnvironmentalContext,
} from "../../utils/environmentalContext";

const ENVIRONMENTAL_METRIC_CARDS = [
  {
    key: "aqi",
    titleKey: "home.environment.aqi",
    defaultTitle: "Air quality",
    icon: "air",
  },
  {
    key: "temperature",
    titleKey: "home.environment.temperature",
    defaultTitle: "Temperature",
    icon: "thermostat",
  },
  {
    key: "humidity",
    titleKey: "home.environment.humidity",
    defaultTitle: "Humidity",
    icon: "water_drop",
  },
  {
    key: "pollen",
    titleKey: "home.environment.pollen",
    defaultTitle: "Pollen",
    icon: "grass",
  },
];

function roundNumericValue(value, decimals = 0) {
  if (typeof value !== "number") {
    return value;
  }

  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function formatObservation(metricKey, observation) {
  if (!observation || observation.value === null || observation.value === undefined) {
    return "--";
  }

  const unit = String(observation.unit || "").trim().toUpperCase();

  switch (metricKey) {
    case "temperature":
      return unit === "FAHRENHEIT"
        ? `${roundNumericValue(observation.value, 0)}°F`
        : `${roundNumericValue(observation.value, 0)}°C`;
    case "humidity":
      return `${roundNumericValue(observation.value, 0)}%`;
    case "aqi":
    case "pollen":
      return String(roundNumericValue(observation.value, 0));
    default:
      return observation.unit
        ? `${roundNumericValue(observation.value, 1)} ${observation.unit}`
        : String(roundNumericValue(observation.value, 1));
  }
}

function EnvironmentalContextLoadingGrid({ loadingLabel }) {
  return (
    <div role="status" aria-live="polite" className="w-full">
      <span className="sr-only">{loadingLabel}</span>
      <div className="grid grid-cols-2 gap-2" aria-hidden="true">
        {ENVIRONMENTAL_METRIC_CARDS.map((card) => (
          <article
            key={`skeleton-${card.key}`}
            data-testid="environmental-card-skeleton"
            className="flex h-full flex-col gap-4 rounded-2xl bg-card px-3 py-3 shadow text-left"
          >
            <div className="flex min-w-0 items-center gap-2">
              <SkeletonBlock className="h-6 w-6 rounded-full" />
              <SkeletonBlock className="h-4 w-24 rounded-full" />
            </div>
            <div className="mt-auto flex items-end justify-between gap-3">
              <SkeletonBlock className="h-5 w-12 rounded-full" />
              <SkeletonBlock className="h-10 w-16 rounded-2xl" />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function HomeEnvironmentalContextCard({ refreshKey = 0 }) {
  const { t } = useTranslation();
  const { weatherDetailsEnabled } = useUserWebAppPresentationFeatures();
  const environmentalContextVisible =
    isEnvironmentalContextEnabled() && weatherDetailsEnabled;
  const [state, setState] = useState({
    loading: environmentalContextVisible,
    context: null,
    error: null,
  });

  useEffect(() => {
    if (!environmentalContextVisible) {
      setState({ loading: false, context: null, error: null });
      return undefined;
    }

    let cancelled = false;
    setState((currentState) => ({
      ...currentState,
      loading: true,
      error: null,
    }));

    captureCurrentEnvironmentalContext({
      purpose: "home",
      includeDailyTimelines: true,
    })
      .then((context) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          context: context || null,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          context: null,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [environmentalContextVisible, refreshKey]);

  const summary = useMemo(
    () => summarizeEnvironmentalContext(state.context),
    [state.context],
  );
  const title = useMemo(() => {
    const displayName = state.context?.location?.displayName;

    if (displayName) {
      return t("home.environment.titleWithLocation", {
        defaultValue: "Conditions in {{location}} today",
        location: displayName,
      });
    }

    return t("home.environment.title", "Today's local conditions");
  }, [state.context, t]);
  const visibleMetricCards = useMemo(() => ENVIRONMENTAL_METRIC_CARDS, []);
  const timelineMetrics = state.context?.timelines?.currentDay?.metrics || null;
  const currentHourOffset =
    state.context?.timelines?.currentDay?.currentHourOffset ?? null;

  if (!environmentalContextVisible) {
    return null;
  }

  return (
    <section className="w-full flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-bold text-typography">{title}</h3>
      </div>

      {state.loading ? (
        <EnvironmentalContextLoadingGrid
          loadingLabel={t(
            "home.environment.loading",
            "Loading environmental context…",
          )}
        />
      ) : null}

      {!state.loading && state.error ? (
        <p className="muted-text text-sm">
          {t(
            "home.environment.unavailable",
            "Environmental context is unavailable right now.",
          )}
        </p>
      ) : null}

      {!state.loading && !state.error && state.context ? (
        <div className="grid grid-cols-2 gap-2">
          {visibleMetricCards.map((card) => {
            const observation = summary[card.key];
            const valueClass = getEnvironmentalMetricValueClass(
              card.key,
              observation,
            );
            const timeline = timelineMetrics?.[card.key] || null;

            return (
              <article
                key={card.key}
                data-testid={`environmental-card-${card.key}`}
                className="flex h-full flex-col gap-4 rounded-2xl bg-card px-3 py-3 shadow text-left"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="material-symbols-outlined text-[22px] leading-none text-primary"
                    aria-hidden="true"
                  >
                    {card.icon}
                  </span>
                  <p className="truncate whitespace-nowrap text-sm font-semibold leading-tight text-typography">
                    {t(card.titleKey, card.defaultTitle)}
                  </p>
                </div>
                <div className="mt-auto flex items-end justify-between gap-3">
                  <p className={`text-base font-semibold ${valueClass}`}>
                    {formatObservation(card.key, observation)}
                  </p>
                  <div className="flex shrink-0 items-end">
                    <EnvironmentalMetricDayChart
                      metricKey={card.key}
                      timeline={timeline}
                      currentHourOffset={currentHourOffset}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      <p className="muted-text text-[11px] leading-relaxed">
        {t(
          "home.environment.subtitle",
          "Context only. These values do not provide clinical advice or diagnosis.",
        )}
      </p>
    </section>
  );
}
